'use client'

import { cn } from '../../utils/cn'

export interface SetupStep {
  id: string
  label: string
  description: string
  status: 'pending' | 'active' | 'completed'
  action?: { label: string; onClick: () => void }
  icon?: React.ReactNode
}

export interface WorkspaceSetupProps {
  title?: string
  steps: SetupStep[]
  className?: string
}

export function WorkspaceSetup({ title, steps, className }: WorkspaceSetupProps) {
  const completed = steps.filter(s => s.status === 'completed').length
  const progress = steps.length > 0 ? (completed / steps.length) * 100 : 0

  return (
    <div className={cn('rounded-xl border border-border bg-card', className)}>
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">{title || 'Setup'}</h3>
          <span className="text-xs text-muted-foreground">{completed} of {steps.length} complete</span>
        </div>
        <div className="h-1.5 rounded-full bg-border overflow-hidden">
          <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Step cards */}
      <div className="p-4 space-y-3">
        {steps.map((step, i) => (
          <div
            key={step.id}
            className={cn(
              'rounded-lg border p-4 transition-all',
              step.status === 'active' && 'border-primary/30 bg-primary/5',
              step.status === 'completed' && 'border-border bg-muted/20 opacity-60',
              step.status === 'pending' && 'border-border bg-card',
            )}
          >
            <div className="flex items-start gap-3">
              {/* Number badge */}
              <div className={cn(
                'flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold shrink-0 mt-0.5',
                step.status === 'completed' && 'bg-primary/20 text-primary',
                step.status === 'active' && 'bg-primary text-primary-foreground',
                step.status === 'pending' && 'bg-muted text-muted-foreground',
              )}>
                {step.status === 'completed' ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg>
                ) : i + 1}
              </div>
              <div className="flex-1">
                <h4 className={cn('text-sm font-semibold', step.status === 'pending' ? 'text-muted-foreground' : 'text-foreground')}>
                  {step.label}
                </h4>
                <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                {step.status === 'active' && step.action && (
                  <button
                    onClick={step.action.onClick}
                    className="mt-3 px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  >
                    {step.action.label}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
